import type { Competition } from '@uefadigital/panenka';

import type { CompetitionSeason } from '@shared-types/competition.types';
import type { RoundPhase, Team, Round, CompetitionStructure } from '@shared-types/match.types';
import { loadFromAPIs, recursiveCall } from '@utilities/fetch';
import { apiKey, compApiUrl, domesticApiUrl } from '@utilities/WindowContext';

export const fetchTeamCompetitions = (
  params: { competitionIds?: string[]; teamId?: string; limit?: number },
  signal?: AbortSignal,
) => loadFromAPIs<Competition[]>('v2/competitions', params, { apiKey, apiUrl: compApiUrl, signal });

export const fetchDomesticCompetitions = (
  params: {
    age?: string;
    competitionType?: string;
    countryCode?: string;
    sex?: 'MALE' | 'FEMALE';
    teamId?: string;
    limit?: number;
    offset?: number;
  },
  signal?: AbortSignal,
) => recursiveCall<Competition>('v2/competitions', params, { apiKey, apiUrl: domesticApiUrl, signal });

export const fetchRoundParticipants = async (
  params: {
    roundIds?: string | string[];
    competitionId?: string;
    seasonYear?: string;
    phase?: RoundPhase | 'ALL';
    teamId?: string;
  },
  signal?: AbortSignal,
) =>
  (await loadFromAPIs<{ teams: Team[]; round: Round }[]>('v2/round-participants', params, {
    apiKey,
    apiUrl: compApiUrl,
    signal,
  })) ?? [];

export const fetchCompetitionStructure = async (
  params: {
    competitionId: string;
    seasonYears?: string | string[];
    phase?: RoundPhase | 'ALL';
  },
  signal?: AbortSignal,
) =>
  (
    await loadFromAPIs<CompetitionStructure[]>('v2/competition-structure', params, {
      apiKey,
      apiUrl: compApiUrl,
      signal,
    })
  )?.[0];

export const fetchRounds = async (
  params: {
    competitionId: string;
    seasonYear?: string | string[];
    phase?: RoundPhase | 'ALL';
  },
  signal?: AbortSignal,
) =>
  await loadFromAPIs<Round[]>('v2/rounds', params, {
    apiKey,
    apiUrl: compApiUrl,
    signal,
  });

export const fetchCompetitionSeasons = async (competitionId: string, signal?: AbortSignal) =>
  (await loadFromAPIs<CompetitionSeason[]>(
    `v2/competitions/${competitionId}/seasons`,
    {},
    { apiKey, apiUrl: compApiUrl, signal },
  )) ?? [];
